import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
    OptionMarketStatus,
    Participant,
    ParticipantMarketStatus,
    ParticipantMarketType,
    ParticipantOption,
    ParticipantPriceStatus,
    ParticipantStatus,
    ParticipantType,
} from '@cds/betting-offer';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketHelpConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import {
    ForecastComponentMode,
    ForecastParticipantViewModel,
    GreyhoundParticipantInfo,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { NOT_APPLICABLE, trackingConstants } from '@frontend/sports/tracking/feature';
import { HorseRacingCommon } from '@frontend/sports/types/components/sitecore';
import { Store } from '@ngrx/store';
import { cloneDeep, keyBy } from 'lodash-es';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { Observable } from 'rxjs';

import { ParticipantPickType } from '../../../betslip-base/models/pick-models';
import { getDayOfRace } from '../../../horse-racing-shared/helpers';
import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { GreyhoundTrackingService } from '../../common/greyhound-tracking.service';

interface ForecastPlaces {
    first: WinGameParticipant | null;
    second: WinGameParticipant | null;
    third: WinGameParticipant | null;
}

export class ForecastModel {
    first = false;
    second = false;
    third = false;
    any = false;

    checkboxId: { [x in keyof ForecastPlaces]: string } & { any: string } = {
        first: '',
        second: '',
        third: '',
        any: '',
    };

    constructor(
        public participant: WinGameParticipant,
        public option: ParticipantOption,
        mode: ForecastComponentMode,
    ) {
        this.checkboxId = {
            first: `cb-${mode}-${participant.id}-first`,
            second: `cb-${mode}-${participant.id}-second`,
            third: `cb-${mode}-${participant.id}-third`,
            any: `cb-${mode}-${participant.id}-any`,
        };
    }
}

interface ForecastColumn {
    id: keyof ForecastPlaces;
    name: string;
    visible: boolean;
}
@Component({
    selector: 'ms-horses-forecast',
    templateUrl: 'horses-forecast.html',
    standalone: false,
})
export class HorsesForecastComponent implements OnInit, OnChanges {
    @Input() mode: ForecastComponentMode;
    @Input() selectTopTwo: Observable<boolean>;
    @Input() selectTopThree: Observable<boolean>;
    @Input() isGreyhounds = false;
    @Input() participantOptionGroups: { [id: string]: EventOption[] };
    @Input() optionGroup?: EventOptionGroup;
    @Input() greyhoundTrapDogMapping: Record<number, GreyhoundParticipantInfo> = {};
    @Input() trackingSource: string;
    @Input()
    set fixture(fixture: WinGameFixture) {
        this._fixture = fixture;
        this.updateModel();
    }

    get fixture(): WinGameFixture {
        return this._fixture;
    }

    isOptionBet: boolean;
    commonTranslations: HorseRacingCommon;
    forecastColumns: ForecastColumn[];
    forecastModel: ForecastModel[] = [];
    placePicks: ForecastPlaces = { first: null, second: null, third: null };
    anyPicks: WinGameParticipant[] = [];
    isMarketSuspended = false;
    tab: string;
    isracingpostPickDisabled = false;
    racingpostForeastpicks: ForecastParticipantViewModel[];
    racingpostTriastpicks: ForecastParticipantViewModel[];
    trackingOptions: { [key: string]: string };
    anyMessageForGreyhounds: string;
    showMarketHelp: boolean;

    private _fixture: WinGameFixture; // we need timeform data for onFixtureSelectForecast to select top participants.
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private horsesFixturePricingHelper: HorsesFixturePricingHelper,
        private horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        private horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        private stringInterpolationPipe: StringInterpolationPipe,
        private store: Store,
        private greyhoundTrackingService: GreyhoundTrackingService,
        private marketHelpConfig: MarketHelpConfig,
    ) {
        this.commonTranslations = sitecore.horseRacing.common;

        this.forecastColumns = [
            { id: 'first', name: this.commonTranslations['1'], visible: true },
            { id: 'second', name: this.commonTranslations['2'], visible: true },
            { id: 'third', name: this.commonTranslations['3'], visible: false },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.participantOptionGroups && changes.participantOptionGroups.previousValue !== changes.participantOptionGroups.currentValue) {
            this.updateModel();
        }
        this.isOptionBet = this.fixture.isOptionBet;
        this.anyMessageForGreyhounds =
            this.mode === ForecastComponentMode.Forecast
                ? this.sitecore.horseRacing.raceDetails.ForecastAny
                : this.sitecore.horseRacing.raceDetails.TricastAny;
    }

    ngOnInit(): void {
        this.mode = this.mode || ForecastComponentMode.Forecast;
        if (this.mode.toLowerCase() === ForecastComponentMode.Tricast) {
            this.forecastColumns.find((c) => c.id === 'third')!.visible = true;
            if (!this.isGreyhounds) {
                this.selectTopThree.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onFixtureSelectForecast);
            }
        } else {
            if (!this.isGreyhounds) {
                this.selectTopTwo.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onFixtureSelectForecast);
            }
        }

        this.tab = this.mode === ForecastComponentMode.Forecast ? 'forecast' : 'tricast';
        if (this.optionGroup) {
            this.showMarketHelp = this.marketHelpConfig.enabled && !!this.optionGroup?.detailedGrouping?.marketHelpPath;
        }
    }

    forecastChange = () => {
        this.placePicks = this.getPlacePicks(this.forecastModel);
    };

    anyColumnChange = () => {
        this.anyPicks = this.getAnyPicks(this.forecastModel);
    };

    onForecastChange(value: boolean, index: number, key: string) {
        if (Object.keys(this.forecastModel[index]).includes(key)) {
            Object.assign(this.forecastModel[index], { [key]: value });
            this.forecastChange();
        }
    }

    onAnyColumnChange(value: boolean, index: number, key: string) {
        if (Object.keys(this.forecastModel[index]).includes(key)) {
            Object.assign(this.forecastModel[index], { [key]: value });
            this.anyColumnChange();
        }
    }

    participantHighlight(model: ForecastModel): boolean {
        return this.isPlaceAssigned(model) || model.any === true;
    }

    isPlaceColumnDisabled(model: ForecastModel, column: string): boolean {
        if (this.anyPicks.length > 0) {
            // If any picks are selected then we can select only place, so disable place columns
            return true;
        }

        const participantHasPlaceAssigned = this.isPlaceAssigned(model);
        const placeHasParticipantAssigned = this.placePicks[column as keyof ForecastPlaces] !== null;
        const checkboxSelected = model[column as keyof ForecastPlaces];

        return (participantHasPlaceAssigned || placeHasParticipantAssigned) && checkboxSelected === false;
    }

    isAnyColumnDisabled(): boolean {
        // if at lease one input from place column is selected, then any is disabled
        return this.forecastColumns.map((c) => this.placePicks[c.id as keyof ForecastPlaces]).some((pick) => pick !== null);
    }

    validPick(): boolean {
        if (this.anyPicks.length > 0) {
            return this.anyPicks.length >= this.minimumPickCount;
        }

        if (this.isGreyhounds) {
            this.setRacingpostPicks();
        }

        return this.forecastColumns
            .filter((c) => c.visible)
            .map((c) => this.placePicks[c.id as keyof ForecastPlaces])
            .every((place) => place !== null);
    }

    private setRacingpostPicks(): void {
        if (this.mode === ForecastComponentMode.Forecast) {
            if (this.placePicks.first && this.placePicks.second) {
                if (
                    this.racingpostForeastpicks.length &&
                    this.racingpostForeastpicks[0].participantId === this.placePicks.first.participantId &&
                    this.racingpostForeastpicks[1].participantId === this.placePicks.second.participantId
                ) {
                    this.isracingpostPickDisabled = true;
                }
            } else {
                this.isracingpostPickDisabled = false;
            }
        } else if (this.mode === ForecastComponentMode.Tricast) {
            if (this.placePicks.first && this.placePicks.second && this.placePicks.third) {
                if (
                    this.racingpostTriastpicks.length &&
                    this.racingpostTriastpicks[0].participantId === this.placePicks.first.participantId &&
                    this.racingpostTriastpicks[1].participantId === this.placePicks.second.participantId &&
                    this.racingpostTriastpicks[2].participantId === this.placePicks.third.participantId
                ) {
                    this.isracingpostPickDisabled = true;
                }
            } else {
                this.isracingpostPickDisabled = false;
            }
        }
    }

    getInfoMessage(): string {
        return this.mode === ForecastComponentMode.Forecast
            ? this.sitecore.horseRacing.raceDetails.ForecastMinBets
            : this.sitecore.horseRacing.raceDetails.TricastMinBets;
    }

    getPickSummary(): string {
        if (this.anyPicks.length >= 2) {
            const permutations = this.permutations(this.anyPicks.length, this.minimumPickCount);

            return this.mode === ForecastComponentMode.Forecast
                ? this.stringInterpolationPipe.transform(this.sitecore.horseRacing.raceDetails.ForcastCombinations, { permutations })
                : this.stringInterpolationPipe.transform(this.sitecore.horseRacing.raceDetails.TricastCombinations, { permutations });
        }

        return this.mode === ForecastComponentMode.Forecast
            ? this.sitecore.horseRacing.raceDetails.ForecastStraight
            : this.sitecore.horseRacing.raceDetails.TricastStraight;
    }

    canClearPicks(): boolean {
        const anyPicked = this.anyPicks.length > 0;
        const forecastPicked = this.forecastColumns.map((c) => this.placePicks[c.id as keyof ForecastPlaces]).some((place) => place !== null);

        return anyPicked || forecastPicked;
    }

    clearPicks(): void {
        this.greyhoundTrackingService.trackClear(this.fixture, this.tab === ForecastComponentMode.Forecast);
        this.clearSelections();
    }

    private clearSelections(): void {
        this.forecastModel.forEach((model) => {
            model.first = false;
            model.second = false;
            model.third = false;
            model.any = false;
        });

        this.forecastChange();
        this.anyColumnChange();
    }

    hasTimeFormRatings(fixture: WinGameFixture) {
        return fixture.participants
            .filter((o: WinGameParticipant) => !!o.timeformInfo?.analystRating)
            .some((o: WinGameParticipant) => o.timeformInfo?.analystRating);
    }

    betslipAdd(): void {
        if (this.isGreyhounds && !this.isOptionBet) {
            this.greyhoundsBetslipAdd();

            return;
        }
        this.trackingOptions = this.getTrackingOptions(this.isGreyhounds);

        const pickTrackingSource =
            this.tab === ForecastComponentMode.Forecast
                ? !this.isGreyhounds
                    ? 'horseracing_Racecard/forecast'
                    : this.trackingSource + '/forecast'
                : !this.isGreyhounds
                  ? 'horseracing_Racecard/tricast'
                  : this.trackingSource + '/tricast';

        const pickId = this.isOptionBet
            ? this.horsesFixtureParticipantHelper.createOptionPick(
                  this.fixture,
                  this.selectedParticipants,
                  this.marketType,
                  this.betType,
                  this.participantOptionGroups,
                  this.optionGroup,
              )
            : this.horsesFixtureParticipantHelper.createParticipantPick(this.fixture, this.selectedParticipants, this.marketType, this.betType);

        this.addPick(pickId, pickTrackingSource, this.trackingOptions);
    }

    private addPick(pickId: any, trackingSource: string, trackingOptions: any): void {
        if (pickId) {
            this.store.dispatch(
                ExternalBetslipActions.addPick({
                    pickId,
                    tracking: {
                        source: trackingSource,
                        additional: trackingOptions,
                        moduleType: NOT_APPLICABLE,
                    },
                }),
            );
            this.clearSelections();
        }
    }

    private greyhoundsBetslipAdd(): void {
        const trackingSource = this.tab === ForecastComponentMode.Forecast ? 'RacePage/Forecast' : 'RacePage/Tricast';
        this.trackingOptions = this.getTrackingOptions(true);
        const pickId = this.horsesFixtureParticipantHelper.createParticipantPick(
            this.fixture,
            this.selectedParticipants,
            this.marketType,
            this.betType,
        );
        this.addPick(pickId, trackingSource, this.trackingOptions);
    }

    private getAnyPicks(viewModel: ForecastModel[]): WinGameParticipant[] {
        if (!this.isOptionBet) {
            return viewModel
                .filter((model) => model.any && this.horsesFixturePricingHelper.isPriceStatusVisible(model.participant, this.marketType))
                .map((e) => e.participant);
        } else {
            return viewModel.filter((model) => model.any && this.isDerivedMarketPriceStatusVisible(model.participant)).map((e) => e.participant);
        }
    }

    private getPlacePicks(viewModel: ForecastModel[]): ForecastPlaces {
        const result: ForecastPlaces = { first: null, second: null, third: null };

        if (!this.isOptionBet) {
            viewModel.forEach((model) => {
                Object.keys(result).forEach((key) => {
                    if (
                        model[key as keyof ForecastPlaces] &&
                        this.horsesFixturePricingHelper.isPriceStatusVisible(model.participant, this.marketType)
                    ) {
                        result[key as keyof ForecastPlaces] = model.participant;
                    }
                });
            });
        } else {
            viewModel.forEach((model) => {
                Object.keys(result).forEach((key) => {
                    if (model[key as keyof ForecastPlaces] && this.isDerivedMarketPriceStatusVisible(model.participant)) {
                        result[key as keyof ForecastPlaces] = model.participant;
                    }
                });
            });
        }

        return result;
    }

    priceStatusVisibleOrSuspended(price: ParticipantOption): boolean {
        return (
            (price && price.participantPriceStatus === ParticipantPriceStatus.Visible) ||
            (price && price.participantPriceStatus === ParticipantPriceStatus.Suspended)
        );
    }

    priceStatusSuspended(price: ParticipantOption): boolean {
        return !this.isOptionBet ? price && price.participantPriceStatus === ParticipantPriceStatus.Suspended : false;
    }

    get minimumPickCount(): number {
        return this.mode === ForecastComponentMode.Forecast ? 2 : 3;
    }

    private isPlaceAssigned(model: ForecastModel): boolean {
        return model.first || model.second || model.third;
    }

    private updateModel(): void {
        this.isOptionBet = this.fixture.isOptionBet;
        if (this.fixture && this.fixture.participants) {
            this.isMarketSuspended =
                !this.isOptionBet &&
                this.fixture.participants
                    .flatMap((p) => p.options)
                    .filter((o) => o.marketType === this.marketType)
                    .some((o) => o.marketStatus === ParticipantMarketStatus.Suspended);

            if (this.isOptionBet && this.optionGroup) {
                this.isMarketSuspended =
                    this.fixture.optionMarkets.find((o) => o.id.toString() === this.optionGroup?.id)?.status === OptionMarketStatus.Suspended;
            }

            this.forecastModel = this.updateParticipantsForExistingPicks(this.getValidParticipants(this.fixture.participants));
            this.anyColumnChange();
            this.forecastChange();
        }
    }

    private onFixtureSelectForecast = () => {
        this.clearSelections();
        this.forecastModel.forEach((model) => {
            const analystRating = model.participant.timeformInfo?.analystRating;
            switch (analystRating) {
                case 1:
                    model.first = true;
                    break;
                case 2:
                    model.second = true;
                    break;
                case 3:
                    if (this.mode === 'tricast') {
                        model.third = true;
                    }
                    break;
            }
        });

        this.anyColumnChange();
        this.forecastChange();
    };

    private getValidParticipants(participants: WinGameParticipant[]): WinGameParticipant[] {
        if (!participants || !participants.length) {
            return [];
        }

        let runners = this.horsesFixtureParticipantHelper.getParticipants(participants);
        runners = runners.filter((p) => p.properties!.type === (this.isGreyhounds ? ParticipantType.Trap : ParticipantType.Horse));

        if (this.isGreyhounds) {
            return this.horsesFixtureSortingHelper.sortParticipantsByTrapNumber(runners);
        } else {
            return this.horsesFixtureSortingHelper.sortParticipantsByRaceCardNumber(runners);
        }
    }

    private updateParticipantsForExistingPicks(participants: Array<WinGameParticipant>): ForecastModel[] {
        const lastForecastModelState = keyBy(this.forecastModel, (model) => model.participant.id);

        return participants.map((p) => {
            const option = this.horsesFixturePricingHelper.getPriceForMarketType(p, this.marketType)[0];

            if (option && lastForecastModelState[p.id]) {
                const model = lastForecastModelState[p.id];
                model.participant = p;
                model.option = option;
                if (option.participantPriceStatus !== ParticipantPriceStatus.Visible) {
                    // reset the model if price is not visible
                    return new ForecastModel(p, option, this.mode);
                }

                return model;
            }

            return new ForecastModel(p, option, this.mode);
        });
    }

    racingpostpicks(racingpostpick: ForecastParticipantViewModel[]) {
        this.racingpostTriastpicks = racingpostpick;
        this.racingpostForeastpicks = cloneDeep(this.racingpostTriastpicks).splice(0, 2);
        this.racingpostTriastpicks = this.racingpostTriastpicks.length === 3 ? cloneDeep(this.racingpostTriastpicks).splice(0, 3) : [];
    }

    private get selectedParticipants(): WinGameParticipant[] {
        const simpleBetTypes = [ParticipantPickType.Forecast, ParticipantPickType.Tricast];
        if (simpleBetTypes.indexOf(this.betType) > -1) {
            const selectedParticipants = [this.placePicks.first!, this.placePicks.second!];
            if (this.placePicks.third) {
                selectedParticipants.push(this.placePicks.third);
            }

            return selectedParticipants;
        }

        return this.anyPicks;
    }

    private get betType(): ParticipantPickType {
        switch (this.marketType) {
            case ParticipantMarketType.Forecast:
                return this.anyPicks.length ? ParticipantPickType.CombinationForecast : ParticipantPickType.Forecast;
            case ParticipantMarketType.Tricast:
                return this.anyPicks.length ? ParticipantPickType.CombinationTricast : ParticipantPickType.Tricast;
            default:
                throw new Error(`Unknown horse race market type: ${this.marketType}`);
        }
    }

    private get marketType(): ParticipantMarketType {
        return this.mode === ForecastComponentMode.Forecast ? ParticipantMarketType.Forecast : ParticipantMarketType.Tricast;
    }

    getTrackingOptions(isGreyhounds: boolean) {
        const date = new Date(this.fixture.startDate);

        return {
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: this.fixture.meeting.name.value,
            [trackingConstants.COMPONENT_MODULE_NAME]: !isGreyhounds ? 'horseracing_Racecard' : this.trackingSource,
            [trackingConstants.COMPONENT_MODULE_POSITION]: date.getHours() + ':' + date.getMinutes(),
            [trackingConstants.COMPONENT_MODULE_SOURCE]: '',
            [trackingConstants.COMPONENT_BET_SOURCE]:
                this.tab === ForecastComponentMode.Forecast
                    ? !isGreyhounds
                        ? 'horseracing_Racecard/forecast'
                        : this.trackingSource + '/forecast'
                    : !isGreyhounds
                      ? 'horseracing_Racecard/tricast'
                      : this.trackingSource + '/tricast',
            [trackingConstants.BET_CALENDER]: getDayOfRace(this.fixture.startDate),
        };
    }

    private isDerivedMarketPriceStatusVisible(participant: Participant): boolean {
        return participant.status === ParticipantStatus.NotDefined;
    }

    private permutations(n: number, r: number): number {
        if (n < 0) {
            throw new Error('n-param should be either positive or equal to zero.');
        }

        if (r < 0) {
            throw new Error('r-param should be either positive or equal to zero.');
        }

        if (n < r) {
            return 0;
        }

        const fact = [1]; // reduce on empty array throws

        for (let divident = n; divident > n - r; divident--) {
            fact.push(divident);
        }

        return fact.reduce((curr, next) => curr * next);
    }
}
